// Point d'entrée — expose une API impérative (window.EtekaBot)
// + définit les custom elements <eteka-bot> (chat) et <eteka-form> (formulaire).

import { EtekaBot, type EtekaBotOptions } from './EtekaBot';
import { EtekaForm, type EtekaFormOptions } from './EtekaForm';
import { installWixTracking, setTrackingApiUrl } from './wixTracking';

// Origine du script eteka.js = même origine que l'API Vercel (/event, /profile).
// Permet au pixel Wix de fonctionner sans configuration supplémentaire.
function detectApiOrigin(): string {
  try {
    const current = document.currentScript as HTMLScriptElement | null;
    const src =
      current?.src ??
      document.querySelector<HTMLScriptElement>('script[src*="eteka.js"]')?.src;
    return src ? new URL(src).origin : '';
  } catch {
    return '';
  }
}

const api = {
  /** Monte le bot en mode CHAT dans un élément. */
  mount(target: string | HTMLElement, options: EtekaBotOptions = {}): EtekaBot {
    setTrackingApiUrl(options.apiUrl);
    const host = resolve(target);
    return new EtekaBot(host, options);
  },

  /** Monte le bot CHAT en flottant (bottom-right) avec launcher. */
  autoMount(options: EtekaBotOptions = {}): EtekaBot {
    setTrackingApiUrl(options.apiUrl);
    const host = document.createElement('div');
    host.id = 'eteka-bot-host';
    document.body.appendChild(host);
    return new EtekaBot(host, options);
  },

  /** Monte le questionnaire en mode FORMULAIRE (plein écran / inline) dans un élément. */
  mountForm(target: string | HTMLElement, options: EtekaFormOptions = {}): EtekaForm {
    setTrackingApiUrl(options.apiUrl);
    const host = resolve(target);
    return new EtekaForm(host, options);
  },

  /** userId anonyme du quiz (null si le questionnaire n'a jamais été ouvert). */
  getUserId(): string | null {
    try {
      return localStorage.getItem('eteka.userId');
    } catch {
      return null;
    }
  },
};

function resolve(target: string | HTMLElement): HTMLElement {
  const host = typeof target === 'string' ? document.querySelector<HTMLElement>(target) : target;
  if (!host) throw new Error(`Eteka: cible "${target}" introuvable`);
  return host;
}

// Custom element CHAT.
class EtekaBotElement extends HTMLElement {
  private bot: EtekaBot | null = null;
  connectedCallback() {
    if (this.bot) return;
    this.bot = new EtekaBot(this, {
      apiUrl: this.getAttribute('api-url') ?? undefined,
      useConversationalRewrite: this.getAttribute('rewrite') === 'true',
      openOnLoad: this.getAttribute('open') === 'true',
      autoResume: this.getAttribute('resume') !== 'false',
    });
  }
}

// Custom element FORMULAIRE.
class EtekaFormElement extends HTMLElement {
  private form: EtekaForm | null = null;
  connectedCallback() {
    if (this.form) return;
    this.form = new EtekaForm(this, {
      apiUrl: this.getAttribute('api-url') ?? undefined,
      wixMemberId: this.getAttribute('wix-member-id') ?? undefined,
      autoResume: this.getAttribute('resume') !== 'false',
    });
  }
}

if (typeof customElements !== 'undefined') {
  if (!customElements.get('eteka-bot')) customElements.define('eteka-bot', EtekaBotElement);
  if (!customElements.get('eteka-form')) customElements.define('eteka-form', EtekaFormElement);
}

declare global {
  interface Window {
    EtekaBot: typeof api;
  }
}
if (typeof window !== 'undefined') {
  window.EtekaBot = api;
  // Pixel achats Wix : actif dès le chargement du script, même si le bot
  // n'est jamais monté sur la page courante (ex : page de remerciement).
  installWixTracking(detectApiOrigin());
}

export { EtekaBot, EtekaForm };
export type { EtekaBotOptions, EtekaFormOptions };
export const mount = api.mount;
export const autoMount = api.autoMount;
export const mountForm = api.mountForm;
